import * as React from "react"
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog"
import { CircleHelp, Lock, Trash2, TriangleAlert, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { getErrorMessage } from "@/lib/errors"
import { Button } from "@/components/ui/button"

/*
  Confirmations that say what will happen — replaces window.confirm / window.prompt.

    const confirm = useConfirm()
    const ok = await confirm({
      title: 'Delete “C33”?',
      subtitle: 'BMS · Active · created 14 May 2025',
      description: 'This permanently removes the project and everything linked to it:',
      consequences: [{ icon: List, count: 12, label: 'BOQ items' }, '3 invoices'],
      tone: 'danger',
      confirmLabel: 'Delete project',
      requireText: 'C33',
      alternative: { label: 'Set On hold instead', text: 'Finished or paused? Set the status instead — it keeps the history.', onSelect: () => setOnHold() },
      note: 'Only Super Admin can delete projects.',
    })
    if (!ok) return

  `confirmIcon`: the confirm button's icon. Default: a trash can for tone 'danger' (a delete), none otherwise.
  Pass `null` for a danger action that deletes nothing ("Turn off sign-in", "Switch off") — the button then has no
  icon and the header shows a warning sign instead of the trash can — or any lucide icon component.

  Resolves true on confirm, false on cancel / Esc / close / alternative.
  `onConfirm` (async) keeps the dialog open with a busy button until it settles; a thrown error is
  shown inside the dialog (as text — see lib/errors.js). Outside a <ConfirmProvider>, useConfirm falls back to window.confirm.
*/

const ConfirmContext = React.createContext(null)

const toText = (value) => (typeof value === "string" ? value : "")

function ConsequenceList({ items }) {
  if (!items?.length) return null
  return (
    <ul className={cn("grid gap-x-4 gap-y-1.5", items.length > 2 && "sm:grid-cols-2")} data-testid="confirm-consequences">
      {items.map((item, index) => {
        const entry = typeof item === "string" ? { label: item } : item
        const Icon = entry.icon
        return (
          <li key={entry.key || index} className="flex min-w-0 items-center gap-2 text-[13px] font-semibold text-ink">
            {Icon ? <Icon className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden="true" /> : (
              <span className="ui-tone-dot tone-gray shrink-0" aria-hidden="true" />
            )}
            {entry.count !== undefined && entry.count !== null ? <span>{entry.count}</span> : null}
            <span className={cn("truncate", entry.count !== undefined && entry.count !== null && "font-medium text-ink-3")}>
              {entry.label}
            </span>
          </li>
        )
      })}
    </ul>
  )
}

/**
 * Controlled confirm dialog. Most code should use `useConfirm()`; render this directly only when
 * a page needs the dialog inline (e.g. custom open state).
 */
function ConfirmDialog({
  open,
  title = "Are you sure?",
  subtitle,
  description,
  consequences,
  children,
  confirmLabel,
  cancelLabel = "Cancel",
  tone = "default",
  icon,
  confirmIcon,
  requireText,
  requireTextLabel,
  alternative,
  note,
  onConfirm,
  onResolve,
}) {
  const danger = tone === "danger"
  const [typed, setTyped] = React.useState("")
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState("")
  const inputRef = React.useRef(null)
  const inputId = React.useId()

  React.useEffect(() => {
    if (open) {
      setTyped("")
      setBusy(false)
      setError("")
    }
  }, [open])

  const needsText = requireText !== undefined && requireText !== null && String(requireText) !== ""
  const textMatches = !needsText || typed.trim() === String(requireText).trim()
  const canConfirm = textMatches && !busy

  const close = (result) => {
    if (busy) return
    onResolve?.(result)
  }

  const handleConfirm = async () => {
    if (!canConfirm) return
    if (!onConfirm) {
      onResolve?.(true)
      return
    }
    setBusy(true)
    setError("")
    try {
      await onConfirm()
      setBusy(false)
      onResolve?.(true)
    } catch (err) {
      setBusy(false)
      // Always a string: FastAPI 422s send `detail` as an array of objects.
      setError(getErrorMessage(err))
    }
  }

  const handleAlternative = () => {
    if (busy) return
    onResolve?.(false)
    alternative?.onSelect?.()
  }

  const deletes = danger && confirmIcon === undefined
  const ButtonIcon = confirmIcon === undefined ? (danger ? Trash2 : null) : confirmIcon
  const HeaderIcon = icon || (deletes ? Trash2 : danger ? TriangleAlert : CircleHelp)
  const resolvedConfirmLabel = confirmLabel || (danger ? "Delete" : "Confirm")

  return (
    <AlertDialogPrimitive.Root open={open} onOpenChange={(next) => { if (!next) close(false) }}>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-[color:var(--scrim)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <AlertDialogPrimitive.Content
          data-testid="confirm-dialog"
          data-tone={danger ? "danger" : "default"}
          onOpenAutoFocus={(event) => {
            if (needsText) {
              event.preventDefault()
              inputRef.current?.focus()
            }
          }}
          onEscapeKeyDown={(event) => { if (busy) event.preventDefault() }}
          className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100dvh-32px)] w-[calc(100vw-32px)] max-w-[520px] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[18px] border border-line bg-surface text-ink shadow-overlay duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
        >
          <div className="flex items-start gap-3 px-5 pb-1 pt-[18px]">
            <span className={cn("ui-icon-tile", danger ? "tone-red" : "tone-navy")} aria-hidden="true">
              <HeaderIcon className="h-[18px] w-[18px]" />
            </span>
            <div className="min-w-0 flex-1 pt-0.5">
              <AlertDialogPrimitive.Title className="font-heading text-[17px] font-bold leading-snug text-ink">
                {title}
              </AlertDialogPrimitive.Title>
              {subtitle ? <div className="mt-0.5 text-[12.5px] text-ink-3">{subtitle}</div> : null}
            </div>
            <button
              type="button"
              onClick={() => close(false)}
              aria-label="Close"
              className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-lg text-ink-3 transition-colors hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          <div className="flex flex-col gap-3 px-5 pb-4 pt-3 text-[13.5px] text-ink-2">
            {description ? (
              <AlertDialogPrimitive.Description asChild>
                <div>{description}</div>
              </AlertDialogPrimitive.Description>
            ) : (
              <AlertDialogPrimitive.Description className="sr-only">{toText(title)}</AlertDialogPrimitive.Description>
            )}
            <ConsequenceList items={consequences} />
            {children}
            {alternative ? (
              <div className="flex items-center gap-3 rounded-xl border border-[color:var(--brand-soft-border)] bg-brand-soft py-2.5 pl-3 pr-2.5 text-[12.5px] text-ink-2">
                <span className="min-w-0 flex-1">{alternative.text}</span>
                <Button type="button" variant="outline" size="sm" onClick={handleAlternative} disabled={busy}>
                  {alternative.label}
                </Button>
              </div>
            ) : null}
            {needsText ? (
              <div className="flex flex-col gap-1.5">
                <label htmlFor={inputId} className="text-[12.5px] font-semibold text-ink-2">
                  {requireTextLabel || (
                    <>Type <code className="rounded bg-[color:var(--status-gray-bg)] px-1.5 py-px text-[12px] text-ink">{String(requireText)}</code> to confirm</>
                  )}
                </label>
                <input
                  id={inputId}
                  ref={inputRef}
                  value={typed}
                  onChange={(event) => setTyped(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault()
                      handleConfirm()
                    }
                  }}
                  autoComplete="off"
                  spellCheck={false}
                  data-testid="confirm-require-text"
                  className="h-9 rounded-control border border-line bg-surface px-3 font-mono text-[13px] text-ink outline-none transition-shadow focus:border-[color:var(--lime-600)] focus:shadow-focus"
                />
              </div>
            ) : null}
            {error ? (
              <div role="alert" className="flex items-start gap-2 rounded-xl border border-[color:var(--danger-border)] bg-status-red-bg px-3 py-2 text-[12.5px] text-status-red-ink">
                <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span>{error}</span>
              </div>
            ) : null}
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-line-2 px-5 py-3 sm:flex-row sm:justify-end">
            <AlertDialogPrimitive.Cancel asChild>
              <Button type="button" variant="outline" disabled={busy} data-testid="confirm-cancel">
                {cancelLabel}
              </Button>
            </AlertDialogPrimitive.Cancel>
            <Button
              type="button"
              variant={danger ? "destructive" : "primary"}
              onClick={handleConfirm}
              disabled={!canConfirm}
              data-testid="confirm-accept"
            >
              {ButtonIcon && !busy ? <ButtonIcon aria-hidden="true" /> : null}
              {busy ? "Working…" : resolvedConfirmLabel}
            </Button>
          </div>

          {note ? (
            <div className="flex items-center gap-1.5 border-t border-line-2 bg-surface-2 px-5 py-2 text-[12px] text-ink-3">
              <Lock className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span>{note}</span>
            </div>
          ) : null}
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  )
}

/** Mount once near the app root (App.js). Provides `useConfirm()`. */
function ConfirmProvider({ children }) {
  const [request, setRequest] = React.useState(null)
  const [open, setOpen] = React.useState(false)
  const resolverRef = React.useRef(null)
  const requestIdRef = React.useRef(0)

  const settle = React.useCallback((result) => {
    const resolve = resolverRef.current
    resolverRef.current = null
    setOpen(false)
    resolve?.(Boolean(result))
  }, [])

  const confirm = React.useCallback((options = {}) => {
    const normalized = typeof options === "string" ? { title: options } : options
    // A new request replaces a pending one; the old caller gets `false`.
    resolverRef.current?.(false)
    return new Promise((resolve) => {
      resolverRef.current = resolve
      requestIdRef.current += 1
      setRequest({ id: requestIdRef.current, options: normalized })
      setOpen(true)
    })
  }, [])

  React.useEffect(() => () => resolverRef.current?.(false), [])

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {request ? <ConfirmDialog key={request.id} {...request.options} open={open} onResolve={settle} /> : null}
    </ConfirmContext.Provider>
  )
}

// Only used when a component renders outside <ConfirmProvider> (e.g. an isolated unit test).
// The app always mounts the provider, so real users always get the full dialog.
const fallbackConfirm = async (options = {}) => {
  const normalized = typeof options === "string" ? { title: options } : options
  if (typeof window === "undefined" || typeof window.confirm !== "function") return false
  const message = [toText(normalized.title), toText(normalized.description)].filter(Boolean).join("\n\n")
  if (!window.confirm(message || "Are you sure?")) return false
  if (normalized.onConfirm) await normalized.onConfirm()
  return true
}

/** `const confirm = useConfirm(); if (!(await confirm({...}))) return;` */
function useConfirm() {
  return React.useContext(ConfirmContext) || fallbackConfirm
}

export { ConfirmDialog, ConfirmProvider, useConfirm }
